import { useState } from 'react';
import type { ParsedFaqRow, DuplicateAction, StatusFilter } from '../types';
import { DuplicateActionSelect } from './DuplicateActionSelect';
import { DetailPanel } from './DetailPanel';

interface FaqPreviewTableProps {
  rows: ParsedFaqRow[];
  filter: StatusFilter;
  onToggleSelect: (rowNumber: number) => void;
  onToggleSelectAll: () => void;
  onDuplicateActionChange: (rowNumber: number, action: DuplicateAction) => void;
}

export const FaqPreviewTable: React.FC<FaqPreviewTableProps> = ({
  rows,
  filter,
  onToggleSelect,
  onToggleSelectAll,
  onDuplicateActionChange,
}) => {
  const [detailRow, setDetailRow] = useState<number | null>(null);
  const filtered = filter === 'all' ? rows : rows.filter((r) => r.status === filter);
  const selectableRows = filtered.filter((r) => r.status !== 'error');
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => r.selected);

  return (
    <div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="w-9 border-b border-gray-200 px-2 py-2.5 text-left">
              <input type="checkbox" checked={allSelected} onChange={onToggleSelectAll} />
            </th>
            <th className="w-10 border-b border-gray-200 px-2 py-2.5 text-left">行</th>
            <th className="w-24 border-b border-gray-200 px-2 py-2.5 text-left">ステータス</th>
            <th className="border-b border-gray-200 px-2 py-2.5 text-left">製品カテゴリ</th>
            <th className="border-b border-gray-200 px-2 py-2.5 text-left">FAQカテゴリ</th>
            <th className="border-b border-gray-200 px-2 py-2.5 text-left">質問</th>
            <th className="w-24 border-b border-gray-200 px-2 py-2.5 text-left">操作</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => (
            <tr
              key={row.rowNumber}
              className={
                row.status === 'error'
                  ? 'bg-red-50'
                  : row.status === 'duplicate'
                    ? 'bg-yellow-50'
                    : ''
              }
            >
              <td className="border-b border-gray-100 px-2 py-2">
                <input
                  type="checkbox"
                  checked={row.selected}
                  disabled={row.status === 'error'}
                  onChange={() => onToggleSelect(row.rowNumber)}
                  className={row.status === 'error' ? 'opacity-40' : ''}
                />
              </td>
              <td className="border-b border-gray-100 px-2 py-2 text-gray-400">{row.rowNumber}</td>
              <td className="border-b border-gray-100 px-2 py-2">
                {row.status === 'new' && (
                  <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                    新規
                  </span>
                )}
                {row.status === 'duplicate' && (
                  <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">
                    重複
                  </span>
                )}
                {row.status === 'error' && (
                  <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    エラー
                  </span>
                )}
              </td>
              <td className="border-b border-gray-100 px-2 py-2">
                {row.status === 'error' ? (
                  <span className="text-red-600">{row.errorMessage}</span>
                ) : (
                  <>
                    <span className={row.isNewCategory ? 'font-medium text-green-600' : ''}>
                      {row.data.categoryName}
                    </span>
                    {row.isNewCategory && (
                      <span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-600">
                        NEW
                      </span>
                    )}
                  </>
                )}
              </td>
              <td className="border-b border-gray-100 px-2 py-2">
                {row.status !== 'error' && (
                  <>
                    <span className={row.isNewFaqCategory ? 'font-medium text-green-600' : ''}>
                      {row.data.faqCategoryName}
                    </span>
                    {row.isNewFaqCategory && (
                      <span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-600">
                        NEW
                      </span>
                    )}
                  </>
                )}
              </td>
              <td className="border-b border-gray-100 px-2 py-2">
                {row.status !== 'error' && row.data.question}
              </td>
              <td className="border-b border-gray-100 px-2 py-2">
                {row.status === 'duplicate' ? (
                  <DuplicateActionSelect
                    value={row.duplicateAction}
                    onChange={(action) => onDuplicateActionChange(row.rowNumber, action)}
                  />
                ) : row.status !== 'error' ? (
                  <button
                    onClick={() => setDetailRow(detailRow === row.rowNumber ? null : row.rowNumber)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    詳細
                  </button>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {detailRow !== null &&
        (() => {
          const row = rows.find((r) => r.rowNumber === detailRow);
          if (!row || row.status === 'error') return null;
          return (
            <DetailPanel
              type="faq"
              rowNumber={row.rowNumber}
              data={row.data as unknown as Record<string, unknown>}
              onClose={() => setDetailRow(null)}
            />
          );
        })()}
    </div>
  );
};
